import { Input } from '@components/ui/input'
import { cn } from '@/utils'
import type { UnitOfMeasure } from '@/lib/units/unit-conversion'
import { handleBlurOnEnter } from '../handlers/blur-on-enter'

interface TransformFieldProps {
  /** Group label above the field ("Position" / "Dimensions"); fields without one align to the row's bottom. */
  label?: string
  /** Letter shown inside the input (X, Y, W, H). */
  letter: string
  value: string
  error?: string
  /** True when this field or its row partner has an error — reserves the error line's height. */
  rowHasError: boolean
  errorId: string
  ariaLabel: string
  onChange: (value: string) => void
  onCommit: (value: string) => void
  disabled: boolean
  unitOfMeasure: UnitOfMeasure
  unitLabel: string
}

/** One X/Y/W/H text input with its unit suffix and inline error. */
export function TransformField({
  label,
  letter,
  value,
  error,
  rowHasError,
  errorId,
  ariaLabel,
  onChange,
  onCommit,
  disabled,
  unitOfMeasure,
  unitLabel,
}: TransformFieldProps) {
  return (
    <div className={label ? 'flex flex-col gap-2' : 'flex flex-col gap-2 justify-end'}>
      {label && <label className="text-sm font-medium text-foreground leading-none">{label}</label>}
      <div className="flex flex-col gap-1">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-normal text-muted-foreground">
            {letter}
          </span>
          <Input
            type="text"
            inputMode="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => onCommit(e.target.value)}
            onKeyDown={handleBlurOnEnter}
            size="sm"
            error={!!error}
            aria-describedby={error ? errorId : undefined}
            data-filled={value.trim() !== '' ? true : undefined}
            className={cn('bg-card border-input pl-7', unitOfMeasure !== 'Picas' && 'pr-9')}
            disabled={disabled}
            aria-label={ariaLabel}
          />
          {unitOfMeasure !== 'Picas' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-normal text-muted-foreground">
              {unitLabel}
            </span>
          )}
        </div>
        <div className={cn(rowHasError && 'h-8 overflow-hidden')}>
          {error && (
            <span id={errorId} role="alert" className="line-clamp-2 text-xs text-destructive">
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
